import React, { useMemo } from "react";
import { Image } from "../../../../../components/Image/Image";
import { Product, ImageType, ProductImageTypes } from "@core/types";
import { CartDataProductsItem } from "../../../../../providers/cartDataStore";
import { useProductCardActions } from "../../../../../providers/useProductActions";

import styles from "./CartProductCard.module.scss";
import classnames from "classnames/bind";
import { AttributeBadge } from "../../../../../fsd/shared/AttributeBadge/AttributeBadge";
import useOpenProductDetailSheet from "../../../../../hooks/useOpenProductDetailSheet";
import { QuantityInput } from "../QuantityInput";
import { useConfig } from "../../../../../providers/config";
import { getProductQuantityStep } from "../../../../../utils/productQuantity";
import { prettifyNumbers } from "../../../../../utils/prettifyNumbers";

interface CartProductCardProps {
  cartItem: CartDataProductsItem;
  className?: string;
  product: Product;
  lazyLoad?: boolean;
}

const cx = classnames.bind(styles);

const CartProductCard = ({ cartItem, product, className, lazyLoad }: CartProductCardProps) => {
  const config = useConfig();
  const cartQuantity = cartItem.quantity || 0;
  const productImage = { ...product?.images[0], type: ProductImageTypes.img } as ImageType;
  const showOriginalQuantity = cartItem.originalQuantity > cartItem.quantity;
  const openProductDetailSheet = useOpenProductDetailSheet();
  const step = getProductQuantityStep({
    product,
  });
  const isDisabled =
    (product.stockQuantity > cartQuantity && product.stockQuantity - cartQuantity < step) ||
    product.stockQuantity === cartQuantity;

  const { addToCart, removeProductFromCart } = useProductCardActions();
  const cartProductTotal = (cartItem.quantity * Number(cartItem.price ?? 0)).toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    compactDisplay: "short",
    maximumFractionDigits: 2,
  });

  const cartProductWeight = (Number(cartItem.weight) * cartItem.quantity).toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    compactDisplay: "short",
    maximumFractionDigits: 2,
  });

  const hasAttributes = !!cartItem.attributes?.length;

  const attributes = useMemo(() => {
    if (!cartItem.attributes?.length) {
      return null;
    }

    return cartItem.attributes.map((attr) => {
      return (
        <div key={attr.id} className="flex items-center content-center gap-1">
          <div className="text-xs">{attr.name}:</div>
          <AttributeBadge value={attr.option ?? ""} isColorPrimary />
        </div>
      );
    });
  }, [cartItem]);

  const handleOpenProductDetail = () => {
    const id = product.type === "variation" ? product.parentId : product.id;
    if (id) {
      openProductDetailSheet({ id: id, productType: product.type });
    }
  };

  return (
    <div
      className="max-h-32 flex items-center justify-between w-full p-2.5 relative !border-b-1 !border-b-solid !border-b-[#e5e7eb] last:!border-b-0"
      onClick={handleOpenProductDetail}
    >
      <div className="flex items-center gap-2.5 w-[80%]">
        <Image
          className="w-[3.75rem] h-[3.75rem] object-cover rounded-md"
          image={productImage}
          placeholder={productImage.base64}
          lazyLoad={false}
        />

        <div className="flex flex-col gap-2.5">
          <span className="text-[#525252] text-xs w-full line-clamp-2">{cartItem.name}</span>
          {hasAttributes && <div className="flex items-center gap-2">{attributes}</div>}
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-between rounded-full  w-[8rem] h-[2.0625rem] bg-[#EAEAEA]">
              <button
                className="text-[#989898] text-2xl w-full"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeProductFromCart(product.id);
                }}
              >
                -
              </button>
              {config.isCartQuantityInputEnabled ? (
                <QuantityInput
                  product={product}
                  cartQuantity={cartQuantity}
                  className="w-full text-center text-[#646464] text-sm font-bold bg-transparent outline-none p-0"
                />
              ) : (
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="w-full text-center text-[#646464] text-sm font-bold"
                >
                  {prettifyNumbers(cartQuantity)}
                </span>
              )}
              <button
                className={cx("text-[#989898] text-2xl w-full ", {
                  "opacity-30": isDisabled,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addToCart(product);
                }}
                disabled={isDisabled}
              >
                +
              </button>
            </div>
            {showOriginalQuantity && (
              <div className="mr-2 line-through font-bold text-[--ion-color-danger-shade]">
                {cartItem.originalQuantity}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-1 mb-1 absolute right-2.5 bottom-3">
        <span className="text-xs sm:text-base text-[#292929]">{cartProductWeight} кг</span>
        <span className="text-xs sm:text-base text-[#292929]">-</span>
        <span className="font-bold text-sm sm:text-base text-[#525252]">{cartProductTotal} ₽</span>
      </div>
    </div>
  );
};

export default CartProductCard;
